import { Component, Input } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { MeasuresFacade } from '@library/store/measures/measures.facade';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { SourceType } from '@library/store/modules/modules.interface';
import { ControlPageCardService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-sidebar/control-page-card.service';
import { ConsumptionUnit, ControlPageStore } from '@pages/control-page/control-page-store.service';
import { combineLatest, filter, map, shareReplay, switchMap } from 'rxjs';

@Component({
  selector: 'app-home-water-card',
  templateUrl: './home-water-card.component.html',
  styleUrls: ['./home-water-card.component.scss']
})
export class HomeWaterCardComponent {

  @Input() selected: boolean;

  private homeWaterUnit$ = this.controlPageStore.homeConsumptionUnit$;

  private modules$ = this.modulesFacade.currentHomeModules$.pipe(
    filter(m => !!m && m.length > 0),
  );

  private data$ = combineLatest({
    hotWater: this.modules$.pipe(
      map(modules => modules.find(m => m.source_type === SourceType.HOT_WATER)),
      filter(m => !!m),
    ),
    coldWater: this.modules$.pipe(
      map(modules => modules.find(m => m.source_type === SourceType.COLD_WATER)),
      filter(m => !!m),
    ),
    state: this.controlPageStore.state$,
  }).pipe(
    shareReplay(),
  );

  EnumMeasureTypes = EnumMeasureTypes;

  private hotWater$ = this.data$.pipe(
    switchMap(({hotWater, state: {scale, timeRange}}) => {
      return this.measuresFacade.getModuleTotal(
        hotWater.id,
        EnumMeasureTypes.SUM_FLUID_CONSUMPTION_HOT_WATER,
        this.controlPageStore.scaleToApiScale(scale),
        timeRange,
      );
    }),
  );

  private coldWater$ = this.data$.pipe(
    switchMap(({coldWater, state: {scale, timeRange}}) => {
      return this.measuresFacade.getModuleTotal(
        coldWater.id,
        EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER,
        this.controlPageStore.scaleToApiScale(scale),
        timeRange,
      );
    }),
  );

  private water$ = combineLatest({
    hotWater: this.hotWater$,
    coldWater: this.coldWater$,
  }).pipe(
    map(({hotWater, coldWater}) => hotWater + coldWater),
    filter(w => !isNaN(w))
  );

  private hotWaterPrice$ = this.data$.pipe(
    switchMap(({hotWater, state: {scale, timeRange}}) => {
      return this.measuresFacade.getModuleTotal(
        hotWater.id,
        EnumMeasureTypes.SUM_FLUID_PRICE_HOT_WATER,
        this.controlPageStore.scaleToApiScale(scale),
        timeRange,
      );
    }),
  );

  private coldWaterPrice$ = this.data$.pipe(
    switchMap(({coldWater, state: {scale, timeRange}}) => {
      return this.measuresFacade.getModuleTotal(
        coldWater.id,
        EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER,
        this.controlPageStore.scaleToApiScale(scale),
        timeRange,
      );
    }),
  );

  private price$ = combineLatest({
    hotWater: this.hotWaterPrice$,
    coldWater: this.coldWaterPrice$,
  }).pipe(
    map(({hotWater, coldWater}) => hotWater + coldWater),
    filter(w => !isNaN(w))
  );

  private measureType$ = this.controlPageStore.homeConsumptionUnit$.pipe(
    map(unit => {
      return unit === ConsumptionUnit.CONSUMPTION
        ? EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER
        : EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER;
    }),
  );

  vm$ = combineLatest({
    home: this.homesFacade.currentHome$,
    consumption: this.water$,
    price: this.price$,
    measureType: this.measureType$,
    unit: this.homeWaterUnit$,
  });

  constructor(
    private controlPageStore: ControlPageStore,
    private modulesFacade: ModulesFacade,
    private measuresFacade: MeasuresFacade,
    private controlPageCardService: ControlPageCardService,
    private homesFacade: HomesFacade,
  ) { }

  selectHomeWater(unit: ConsumptionUnit) {
    this.controlPageCardService.selectHomeWater(unit);
  }

}
